import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { money, monthLabel } from '../../lib/format';
import { monthDateRange, pricedPlan } from '../../lib/plan';
import { BarChart, Frame, HeadRow, Table } from './primitives';
import type { TableColumn } from './primitives';

const COLUMNS: TableColumn[] = [
  { key: 'month', label: 'Month', width: '2fr' },
  { key: 'dates', label: 'Dates', width: '2fr' },
  { key: 'budget', label: 'Budget', align: 'right', width: '1fr' },
];

/**
 * monthly_schedule (brief §4 `13 schedule`): FULL light, table. Rows come
 * straight from the active tier's `PricedPlan.months` (design.md V15
 * derivation: "schedule | `pricing.months` (already sums to grandTotal)"),
 * so the total row foots by construction. A flight over 12 months swaps the
 * table for a `BarChart` strip (brief: "&gt;12 months renders a horizontal
 * bar strip with the same data").
 */
export function MonthlySchedulePage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const plan = pricedPlan(proposal);
  const rows = plan?.months ?? [];
  const useBarStrip = rows.length > 12;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-schedule-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-schedule-body">
          <HeadRow title="Monthly schedule" />
          <div className="mv3-schedule-intro">
            <EditableText
              value={section.content.body}
              field="body"
              placeholder="Add a note about the flight schedule"
              readOnly={readOnly}
              onSave={(body) => actions.saveContent(section, { body })}
            />
          </div>
          {rows.length === 0 ? (
            <p className="note">The monthly schedule appears once the plan has been priced.</p>
          ) : useBarStrip ? (
            <div className="mv3-schedule-bars">
              <BarChart
                seriesA={{ label: 'Budget', values: rows.map((row) => row.total) }}
                height={220}
              />
              <p className="mv3-schedule-bar-caption">
                {rows.length} months · Total budget {money(plan?.grandTotal ?? null)}
              </p>
            </div>
          ) : (
            <Table
              columns={COLUMNS}
              rows={rows.map((row) => ({
                month: (
                  <span className="mv3-schedule-month">
                    <span className="mv3-schedule-num">{String(row.monthIndex).padStart(2, '0')}</span>
                    {monthLabel(proposal.budget.startDate, row.monthIndex)}
                  </span>
                ),
                dates: monthDateRange(proposal.budget.startDate, row.monthIndex) ?? '—',
                budget: money(row.total),
              }))}
              totalRow={{ month: 'Total budget', dates: '', budget: money(plan?.grandTotal ?? null) }}
            />
          )}
        </div>
      </Frame>
    </PageShell>
  );
}
